import { Injectable, signal } from '@angular/core';
import {
  AssetCategory,
  Bill,
  BillRisk,
  ClientFinancesState,
  CreditHealth,
  CreditScoreBand,
  CreditTrendPoint,
  DebtItem,
  DebtType,
  FinancialInsight,
  IncomeSource,
} from './client-finances.model';

// ---------------------------------------------------------------------------
// Deterministic seeding helpers — same djb2 pattern as client-profile.service
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(id: string, key: string, options: T[]): T {
  const h = djb2(`${id}:${key}`);
  return options[h % options.length];
}

function seedInt(id: string, key: string, min: number, max: number): number {
  const h = djb2(`${id}:${key}`);
  return min + (h % (max - min + 1));
}

function seedFloat(id: string, key: string, min: number, max: number, decimals = 2): number {
  const h = djb2(`${id}:${key}`);
  const raw = min + (h % 10000) / 10000 * (max - min);
  return parseFloat(raw.toFixed(decimals));
}

// ---------------------------------------------------------------------------
// Seeding helpers
// ---------------------------------------------------------------------------

function isoDate(daysFromToday: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromToday);
  return d.toISOString().slice(0, 10);
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function seedCreditTrend(id: string, currentScore: number): CreditTrendPoint[] {
  const now = new Date();
  const points: CreditTrendPoint[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = `${MONTH_LABELS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
    const variance = seedInt(id, `trend_${i}`, -30, 30);
    const score = Math.min(850, Math.max(300, currentScore + variance));
    points.push({ month: label, score });
  }
  points[11].score = currentScore;
  return points;
}

function scoreToBand(score: number): CreditScoreBand {
  if (score < 580) return 'Poor';
  if (score < 670) return 'Fair';
  if (score < 740) return 'Good';
  if (score < 800) return 'Very Good';
  return 'Excellent';
}

function seedCreditHealth(id: string): CreditHealth {
  const score = seedInt(id, 'credit_score', 560, 820);
  const band = scoreToBand(score);
  const utilizationPct = seedInt(id, 'utilization', 8, 72);
  const onTimePaymentPct = seedInt(id, 'on_time_pct', 82, 100);

  const recentChanges: string[] = [];
  if (utilizationPct > 30) recentChanges.push('Credit utilization above 30%');
  if (onTimePaymentPct < 95) recentChanges.push('One or more late payments in past 12 months');
  if (score > 750) recentChanges.push('No derogatory marks in past 24 months');
  if (recentChanges.length === 0) recentChanges.push('No significant changes in past 90 days');

  return {
    score,
    band,
    lastUpdated: isoDate(-seedInt(id, 'score_age_days', 1, 28)),
    trend: seedCreditTrend(id, score),
    utilizationPct,
    onTimePaymentPct,
    recentChanges,
  };
}

const LENDERS: Record<DebtType, string[]> = {
  credit_card: ['Chase Sapphire', 'Citi Double Cash', 'Capital One Venture', 'Amex Gold', 'Discover It'],
  medical:     ['Regional Medical Center', 'City Hospital', 'Urgent Care Partners', 'Dental Associates'],
  education:   ['Federal Student Aid', 'Sallie Mae', 'Navient', 'College Ave'],
  personal:    ['Marcus by Goldman', 'LightStream', 'SoFi Personal', 'Upstart'],
};

function seedDebts(id: string): DebtItem[] {
  const count = seedInt(id, 'debt_count', 3, 6);
  const types: DebtType[] = ['credit_card', 'medical', 'education', 'personal'];
  const debts: DebtItem[] = [];
  for (let i = 0; i < count; i++) {
    const type = seedPick<DebtType>(id, `debt_type_${i}`, types);
    const lender = seedPick<string>(id, `debt_lender_${i}`, LENDERS[type]);
    const balance = seedInt(id, `debt_balance_${i}`, 500, 28000);
    const apr = type === 'medical' ? null : seedFloat(id, `debt_apr_${i}`, 4.5, 24.99);
    const minPayment = balance < 1000 ? null : seedInt(id, `debt_min_${i}`, 25, 350);
    const risk = balance > 15000 ? 'high' : balance > 5000 ? 'medium' : 'low';
    debts.push({ id: `debt-${i}`, lender, type, balance, apr, minPayment, risk });
  }
  return debts;
}

function seedIncomeSources(id: string): IncomeSource[] {
  const primary = seedInt(id, 'income_primary', 4500, 18000);
  const hasSide = seedPick(id, 'has_side_income', [true, false, false]);
  const sources: IncomeSource[] = [{ label: 'Primary Employment', amount: primary }];
  if (hasSide) {
    sources.push({ label: 'Freelance / Side Income', amount: seedInt(id, 'income_side', 300, 2500) });
  }
  return sources;
}

function seedAssets(id: string): AssetCategory[] {
  return [
    { category: 'Retirement Accounts (401k/IRA)', value: seedInt(id, 'asset_retirement', 10000, 450000) },
    { category: 'Brokerage / Investments',        value: seedInt(id, 'asset_brokerage',  0,     120000) },
    { category: 'Real Estate Equity',             value: seedInt(id, 'asset_realestate', 0,     350000) },
    { category: 'Cash & Savings',                 value: seedInt(id, 'asset_cash',       1000,  80000)  },
  ].filter(a => a.value > 0);
}

function seedBills(id: string): Bill[] {
  const templates = [
    { name: 'Mortgage / Rent',       category: 'Housing',     baseAmt: [1200, 3500] },
    { name: 'Auto Loan',             category: 'Transport',   baseAmt: [250, 750]   },
    { name: 'Electric & Gas',        category: 'Utilities',   baseAmt: [80, 220]    },
    { name: 'Internet & Phone',      category: 'Utilities',   baseAmt: [60, 180]    },
    { name: 'Health Insurance',      category: 'Insurance',   baseAmt: [200, 650]   },
    { name: 'Streaming Services',    category: 'Subscriptions', baseAmt: [15, 60]   },
    { name: 'Credit Card Minimum',   category: 'Debt',        baseAmt: [35, 400]    },
    { name: 'Student Loan Payment',  category: 'Debt',        baseAmt: [150, 600]   },
  ];
  const count = seedInt(id, 'bill_count', 4, 8);
  const chosen = templates.slice(0, count);
  const risks: BillRisk[] = ['normal', 'normal', 'normal', 'upcoming', 'due_soon', 'overdue'];
  return chosen.map((t, i) => ({
    id: `bill-${i}`,
    name: t.name,
    category: t.category,
    amount: seedInt(id, `bill_amt_${i}`, t.baseAmt[0], t.baseAmt[1]),
    dueDate: isoDate(seedInt(id, `bill_due_${i}`, -3, 30)),
    risk: seedPick<BillRisk>(id, `bill_risk_${i}`, risks),
  }));
}

function deriveInsights(
  id: string,
  state: Omit<ClientFinancesState, 'insights'>
): FinancialInsight[] {
  const insights: FinancialInsight[] = [];
  const { creditHealth, dti, debts, emergencySavings, monthlyExpenses } = state;

  if (creditHealth.utilizationPct > 30) {
    insights.push({
      id: 'ins-utilization',
      icon: 'pi pi-credit-card',
      title: 'High Credit Utilization',
      body: `Utilization is at ${creditHealth.utilizationPct}%. Lenders prefer below 30%. Reducing balances could improve the credit score within 1–2 billing cycles.`,
      suggestedAction: 'Discuss a paydown plan for the highest-balance revolving accounts.',
      severity: creditHealth.utilizationPct > 50 ? 'danger' : 'warn',
    });
  }

  if (dti > 0.36) {
    insights.push({
      id: 'ins-dti',
      icon: 'pi pi-chart-bar',
      title: 'Elevated Debt-to-Income Ratio',
      body: `DTI of ${Math.round(dti * 100)}% exceeds the 36% conventional lending threshold. This may limit refinancing or new credit options.`,
      suggestedAction: 'Explore debt consolidation or accelerated paydown on highest-APR accounts.',
      severity: dti > 0.50 ? 'danger' : 'warn',
    });
  }

  const highRiskDebts = debts.filter(d => d.risk === 'high');
  if (highRiskDebts.length > 0) {
    insights.push({
      id: 'ins-high-debt',
      icon: 'pi pi-exclamation-triangle',
      title: `${highRiskDebts.length} High-Balance Debt${highRiskDebts.length > 1 ? 's' : ''}`,
      body: `${highRiskDebts.map(d => d.lender).join(', ')} represent${highRiskDebts.length === 1 ? 's' : ''} significant balance exposure.`,
      suggestedAction: 'Review payoff timeline and interest cost projections with the client.',
      severity: 'warn',
    });
  }

  const emergencyMonths = monthlyExpenses > 0 ? emergencySavings / monthlyExpenses : 0;
  if (emergencyMonths < 3) {
    insights.push({
      id: 'ins-emergency',
      icon: 'pi pi-shield',
      title: 'Emergency Fund Below Target',
      body: `Current savings cover ${emergencyMonths.toFixed(1)} months of expenses. The recommended minimum is 3–6 months.`,
      suggestedAction: 'Set a monthly savings target to build the emergency fund to 3 months within 12 months.',
      severity: emergencyMonths < 1 ? 'danger' : 'warn',
    });
  }

  const overdueBills = state.bills.filter(b => b.risk === 'overdue');
  if (overdueBills.length > 0) {
    insights.push({
      id: 'ins-overdue',
      icon: 'pi pi-calendar-times',
      title: `${overdueBills.length} Overdue Bill${overdueBills.length > 1 ? 's' : ''}`,
      body: `${overdueBills.map(b => b.name).join(', ')} ${overdueBills.length === 1 ? 'is' : 'are'} past due. Late payments can impact credit score within 30 days.`,
      suggestedAction: 'Confirm client awareness and discuss auto-pay setup to prevent recurrence.',
      severity: 'danger',
    });
  }

  if (creditHealth.band === 'Poor' || creditHealth.band === 'Fair') {
    insights.push({
      id: 'ins-credit-band',
      icon: 'pi pi-star',
      title: 'Credit Score Improvement Opportunity',
      body: `A ${creditHealth.band} score (${creditHealth.score}) limits access to preferred rates. Targeted actions could yield a 40–80 point improvement within 12 months.`,
      suggestedAction: 'Review credit report for errors and discuss a score-building roadmap.',
      severity: 'info',
    });
  }

  if (insights.length === 0) {
    insights.push({
      id: 'ins-healthy',
      icon: 'pi pi-check-circle',
      title: 'Strong Financial Position',
      body: 'Credit health, debt levels, and cash reserves are all within healthy ranges. No immediate concerns identified.',
      suggestedAction: 'Focus conversation on growth opportunities — investing, tax optimization, or estate planning.',
      severity: 'info',
    });
  }

  return insights;
}

function seedFinances(id: string): ClientFinancesState {
  const creditHealth = seedCreditHealth(id);
  const incomeSources = seedIncomeSources(id);
  const monthlyIncome = incomeSources.reduce((s, i) => s + i.amount, 0);
  const debts = seedDebts(id);
  const totalDebtPayments = debts.reduce((s, d) => s + (d.minPayment ?? 0), 0);
  const dti = parseFloat(Math.min(0.75, totalDebtPayments / monthlyIncome).toFixed(3));
  const assets = seedAssets(id);
  const monthlyExpenses = seedInt(id, 'monthly_expenses', 2500, 8000);
  const emergencySavings = seedInt(id, 'emergency_savings', 500, 35000);
  const bills = seedBills(id);

  const partial: Omit<ClientFinancesState, 'insights'> = {
    creditHealth, debts, dti, monthlyIncome, incomeSources,
    assets, emergencySavings, monthlyExpenses, bills,
  };

  return { ...partial, insights: deriveInsights(id, partial) };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable()
export class ClientFinancesStateService {
  private readonly _store = signal<Record<string, ClientFinancesState>>({});

  readonly storeSnapshot = this._store.asReadonly();

  ensureState(sessionId: string): void {
    if (this._store()[sessionId]) return;
    this._store.update(s => ({ ...s, [sessionId]: seedFinances(sessionId) }));
  }
}
